import { useTranslation } from 'react-i18next';
import type { ILocationDrillDownProfession } from '@interfaces/IProductivity';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { formatNumber } from '@utils/formatNumber';
import { ProductivityBar } from '../../Table/ProductivityBar/ProductivityBar';
import styles from './DrillDownProfessions.module.css';

interface IDrillDownProfessionsProps {
  professions: ILocationDrillDownProfession[];
}

/** The professions that worked the floor in the timeframe, one row each:
 *  who, their earned over actual work days, and how many were seen there.
 *  Members with no profession are one "no profession" line, last, so the
 *  lines sum to the floor's figures. */
export function DrillDownProfessions({ professions }: IDrillDownProfessionsProps) {
  const { t } = useTranslation();
  if (professions.length === 0) {
    return <p className={styles.empty}>{t('productivity.drillDown.noProfessions')}</p>;
  }
  return (
    <ul className={styles.list}>
      {professions.map((p) => (
        <li key={p.profession?.professionId ?? 'none'} className={styles.item}>
          <div className={styles.who}>
            {p.profession ? (
              <ProfessionPill profession={p.profession} showContractor compact truncate />
            ) : (
              <span className={styles.noProfession}>{t('productivity.drillDown.noProfession')}</span>
            )}
            <span className={styles.workers}>
              {t('productivity.drillDown.activeWorkers', { count: p.activeWorkers })}
            </span>
          </div>
          <div className={styles.figures}>
            <span className={styles.days}>
              {t('productivity.drillDown.earnedOverActual', {
                earned: formatNumber(p.earnedWorkDays),
                actual: formatNumber(p.actualWorkDays),
              })}
            </span>
            <ProductivityBar value={p.productivity} />
          </div>
        </li>
      ))}
    </ul>
  );
}
